//#region IMPORT
import React from 'react';
import { Button, Dialog } from '../../../../Components/MUIs';
import ConfirmDialog from '../../../../Components/ConfirmDialog';
import {
  Box,
  Stack,
  Chip,
  TextField,
  Typography,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import Canvas from '../../../../Components/Canvas/Canvas';
import FilePreview from '../../../../Components/FilePreview/FilePreview';
/** AG-grid Import */
import 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { BusinessGridGridCommon } from '../../../../Components/BusinessGridGridCommon';
import GridFunctions from '../../../../Services/agGrid/ag-Grid-functions';
import { AgGridReact } from 'ag-grid-react';
import {
  getColumnDefs1,
  getColumnDefs2,
  getColumnDefsDetail,
} from './SAM_BASIC_02_ColumnDefs';
import {
  canDetailApproveReject,
  canDetailSelectDelete,
  syncDetailGridSelectionByDayKey,
} from './SAM_BASIC_02_StatusHelper';
import {
  applyExpandedDetailRowHeight,
  getSamBasic02DetailHeights,
  SAM_BASIC_02_DETAIL_GRID,
} from './SAM_BASIC_02_DetailHeight';

function SamBasic02DetailGrid(props) {
  const { data, context, api: masterApi } = props;
  const rows = data?.detailRows ?? [];
  const pmCd = String(data?.pmCd || data?.PmCd || data?.PM_CD || '').trim();
  const detailCount = rows.length;
  const detailGridRef = React.useRef(null);
  const detailSelectionSyncRef = React.useRef(false);
  const detailSelectedIdsRef = React.useRef(new Set());
  const [selectedDetailRows, setSelectedDetailRows] = React.useState([]);

  const handleDetailSelectionChanged = React.useCallback(() => {
    if (detailSelectionSyncRef.current) return;
    const api = detailGridRef.current?.api;
    if (!api) return;

    const selectedNodes = api.getSelectedNodes?.() ?? [];
    const prev = detailSelectedIdsRef.current;
    let toggledData = null;
    let isSelected = false;

    for (const node of selectedNodes) {
      const id = String(node.id ?? node.rowIndex ?? '');
      if (!prev.has(id)) {
        toggledData = node.data;
        isSelected = true;
        break;
      }
    }
    if (!toggledData) {
      api.forEachNode((node) => {
        const id = String(node.id ?? node.rowIndex ?? '');
        if (prev.has(id) && !node.isSelected()) {
          toggledData = node.data;
          isSelected = false;
        }
      });
    }

    if (toggledData) {
      if (pmCd) {
        context?.setActivePmCd?.(pmCd);
      }
      detailSelectionSyncRef.current = true;
      try {
        syncDetailGridSelectionByDayKey(api, toggledData, isSelected);
      } finally {
        detailSelectionSyncRef.current = false;
      }
    }

    detailSelectedIdsRef.current = new Set(
      (api.getSelectedNodes?.() ?? []).map((n) =>
        String(n.id ?? n.rowIndex ?? '')
      )
    );
    setSelectedDetailRows(api.getSelectedRows?.() ?? []);
  }, [pmCd, context]);

  const refreshMasterDetailHeight = React.useCallback(() => {
    applyExpandedDetailRowHeight(masterApi, pmCd, detailCount);
  }, [masterApi, pmCd, detailCount]);

  React.useEffect(() => {
    const api = detailGridRef.current?.api;
    if (!api) return;
    api.setGridOption('rowData', rows);
    api.deselectAll?.();
    detailSelectedIdsRef.current = new Set();
    setSelectedDetailRows([]);
    const id = window.requestAnimationFrame(() => {
      refreshMasterDetailHeight();
    });
    return () => window.cancelAnimationFrame(id);
  }, [rows, pmCd, refreshMasterDetailHeight]);

  const canAct = canDetailApproveReject(
    selectedDetailRows,
    data ?? {},
    context?.userInfo ?? {}
  );
  const canDelete = canDetailSelectDelete(selectedDetailRows);

  const { panelHeight: detailPanelHeight } =
    getSamBasic02DetailHeights(detailCount);

  React.useLayoutEffect(() => {
    refreshMasterDetailHeight();
  }, [detailCount, detailPanelHeight, refreshMasterDetailHeight]);

  return (
    <Box
      sx={{
        p: 1,
        width: '100%',
        height: detailPanelHeight,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'visible',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 0.5, pr: 0.5, flexShrink: 0 }}
      >
        <Typography variant="caption" color="text.secondary">
          {detailCount > 0
            ? `작업 세부내역 (${detailCount}건) — 같은 작업일자는 함께 선택됩니다. 삭제·반려·승인은 일자 단위로 처리됩니다.`
            : '작업 세부내역이 없습니다. 목록 행을 클릭하면 상세가 조회됩니다.'}
        </Typography>
        <Stack direction="row" gap={0.5}>
          <Button
            name="반려"
            size="small"
            variant="outlined"
            disabled={detailCount === 0 || !canAct}
            onClick={() => {
              if (pmCd) {
                context?.setActivePmCd?.(pmCd);
              }
              context?.onRejectDetailClick?.();
            }}
          />
          <Button
            name="승인"
            size="small"
            variant="outlined"
            disabled={detailCount === 0 || !canAct}
            onClick={() => {
              if (pmCd) {
                context?.setActivePmCd?.(pmCd);
              }
              context?.onApproveDetailClick?.();
            }}
          />
          <Button
            name="선택 삭제"
            size="small"
            color="error"
            variant="outlined"
            disabled={detailCount === 0 || !canDelete}
            onClick={() => {
              if (pmCd) {
                context?.setActivePmCd?.(pmCd);
              }
              context?.onDeleteDetailClick?.();
            }}
          />
          <Button
            name="반려이력"
            size="small"
            variant="outlined"
            disabled={!pmCd}
            onClick={() => {
              if (pmCd) {
                context?.setActivePmCd?.(pmCd);
                context?.onRejectHistoryClick?.(pmCd);
              }
            }}
          />
        </Stack>
      </Stack>
      <Box
        className="ag-theme-alpine"
        sx={{
          width: '100%',
          flexShrink: 0,
          '& .ag-root-wrapper': { borderRadius: 0 },
        }}
      >
        <AgGridReact
          ref={detailGridRef}
          domLayout="autoHeight"
          rowData={rows}
          columnDefs={context?.detailColumnDefs ?? []}
          getRowId={(p) =>
            String(
              p.data?.no ??
                p.data?.dtlCd ??
                p.data?.DtlCd ??
                `${pmCd}-${p.rowIndex}`
            )
          }
          defaultColDef={{
            cellDataType: false,
            minWidth: 72,
            editable: false,
            resizable: true,
            sortable: true,
            filter: false,
            suppressHeaderMenuButton: true,
          }}
          headerHeight={SAM_BASIC_02_DETAIL_GRID.headerHeight}
          rowHeight={SAM_BASIC_02_DETAIL_GRID.rowHeight}
          rowSelection="multiple"
          suppressRowClickSelection={true}
          stopEditingWhenCellsLoseFocus={true}
          suppressCellFocus={true}
          onGridReady={(params) => {
            if (pmCd) {
              context?.registerDetailApi?.(pmCd, params.api);
            }
            refreshMasterDetailHeight();
          }}
          onFirstDataRendered={refreshMasterDetailHeight}
          onRowDataUpdated={refreshMasterDetailHeight}
          onSelectionChanged={handleDetailSelectionChanged}
        />
      </Box>
    </Box>
  );
}

function SAM_BASIC_02(props) {
  //#region 웹
  const expandedPmCdRef = props.expandedPmCdRef;

  /** 목록 재조회 시 마스터 전부 접기 */
  React.useEffect(() => {
    if (props.masterListRefreshKey === undefined) return;
    if (expandedPmCdRef) expandedPmCdRef.current = '';
    const id = window.requestAnimationFrame(() => {
      const api = props.gridRef1?.current?.api;
      if (!api) return;
      api.forEachNode((node) => node.setExpanded(false));
    });
    return () => window.cancelAnimationFrame(id);
  }, [props.masterListRefreshKey, props.gridRef1, expandedPmCdRef]);

  const columnDefsMaster = getColumnDefs1(props);
  const columnDefsRejectHistory = getColumnDefs2();
  const columnDefsDetail = getColumnDefsDetail();

  return (
    <>
      <BusinessGridGridCommon
        getRowId1={(params) =>
          String(
            params.data?.pmCd ??
              params.data?.PmCd ??
              params.data?.PM_CD ??
              params.data?.no
          )
        }
        gridRef1={props.gridRef1}
        columnDefs1={columnDefsMaster}
        rowData1={props.rowData1}
        setSelectedRowData1={props.setSelectedRowData1}
        setChangedRowData1={props.setChangedRowData1}
        title1={'작업허가서 목록'}
        leftSize={50}
        rightSize={50}
        rightContentOnly={
          <Box
            sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
          >
            <Typography sx={{ fontWeight: 'bold', mb: 1, color: '#2CA900' }}>
              작업허가서 미리보기
            </Typography>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              {props.permitPreviewLoading ? (
                <>
                  <LinearProgress
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      zIndex: 2,
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      gap: 1.5,
                      bgcolor: 'rgba(255,255,255,0.88)',
                      zIndex: 1,
                    }}
                  >
                    <CircularProgress size={40} />
                    <Typography variant="body2" color="text.secondary">
                      미리보기 PDF 생성 중입니다…
                    </Typography>
                  </Box>
                </>
              ) : null}
              <FilePreview
                selectedRowData={props.selectedRowData1}
                fileNameField="saveName"
                filePathField="previewFilePath"
                filePreviewHeight="100%"
              />
            </Box>
          </Box>
        }
        gridProps1={{
          masterDetail: true,
          detailCellRenderer: SamBasic02DetailGrid,
          detailCellRendererParams: {},
          context: {
            detailColumnDefs: columnDefsDetail,
            registerDetailApi: props.registerDetailApi,
            setActivePmCd: props.setActivePmCd,
            onDeleteDetailClick: props.onDeleteDetailClick,
            onRejectDetailClick: props.onRejectDetailClick,
            onApproveDetailClick: props.onApproveDetailClick,
            onRejectHistoryClick: props.onRejectHistoryClick,
            userInfo: props.userInfo,
          },
          isRowMaster: (data) => true,
          suppressExpandOnRowClick: true,
          detailRowAutoHeight: false,
          detailRowHeight: (params) =>
            getSamBasic02DetailHeights(params.data?.detailRows?.length ?? 0)
              .panelHeight,
          getRowHeight: (params) => {
            if (!params.node?.detail) return undefined;
            return getSamBasic02DetailHeights(
              params.data?.detailRows?.length ?? 0
            ).panelHeight;
          },
          // 기본은 모두 접힘
          onFirstDataRendered: (params) => {
            if (expandedPmCdRef) expandedPmCdRef.current = '';
            params.api.forEachNode((node) => node.setExpanded(false));
          },
        }}
        addYn1={'N'}
        deleteYn1={'N'}
        editable1={false}
        suppressCellFocus1={true}
        flex={1}
        refreshClick1={() => {
          if (!props.validateSelection()) return;

          props.SearchLogic1(props.searchRef.current.custCd);
        }}
        onCellClicked1={(e) => {
          const gridApi = props.gridRef1.current.api;
          const clickedRowIndex = e.rowIndex;

          const isCheckboxClick = e.event?.target?.type === 'checkbox';

          if (!isCheckboxClick) {
            gridApi.ensureIndexVisible(clickedRowIndex, 'middle');
            gridApi.setFocusedCell(clickedRowIndex, 'reqNm');

            const node =
              e.node ?? gridApi.getDisplayedRowAtIndex(clickedRowIndex);
            const mk =
              e.data?.pmCd ?? e.data?.PmCd ?? e.data?.PM_CD ?? e.data?.no;
            const masterKeyStr = mk != null && mk !== '' ? String(mk) : '';

            const expandedKey = expandedPmCdRef?.current ?? '';

            // 같은 행 재클릭 → 접기만 (상세 재조회 없음)
            if (masterKeyStr && expandedKey === masterKeyStr) {
              gridApi.forEachNode((n) => n.setExpanded(false));
              if (expandedPmCdRef) expandedPmCdRef.current = '';
              return;
            }

            const copied = JSON.parse(JSON.stringify(e.data));
            node?.setSelected(true, false);

            if (expandedPmCdRef) expandedPmCdRef.current = masterKeyStr;
            gridApi.forEachNode((n) => {
              n.setExpanded(
                String(
                  n.data?.pmCd ?? n.data?.PmCd ?? n.data?.PM_CD ?? ''
                ).trim() === masterKeyStr
              );
            });

            props.handleRowSelection1(copied, clickedRowIndex);
          }
        }}
        onSelectionChanged1={(gridRef) => {
          const gridApi = gridRef?.current?.api;
          if (!gridApi) return;
          const selectedNodes = gridApi.getSelectedNodes();
          const selectedData = selectedNodes.map((n) => n.data);
          props.selectedRowNosRef1.current = new Set(
            selectedData.map((r) => r.no)
          );
          props.setSelectedRowData1(selectedData);
        }}
        onCellValueChanged1={(e) => {
          const key = e.data.no;

          props.setChangedRowData1?.((prev) => {
            const filtered = prev.filter((row) => row.no !== key);
            return [...filtered, { ...e.data }];
          });
        }}
        other1={
          <>
            <Button
              name="항목추가"
              onClick={() => {
                props.handleCodeManagementClick();
              }}
            />
            <Button
              name="삭제"
              color="error"
              variant="outlined"
              onClick={() => {
                props.onDeleteMasterPermitClick?.();
              }}
            />
          </>
        }
      />
      {props.modalToggle1 && (
        <div
          role="presentation"
          className="MuiDialog-root MuiModal-root"
          inert="true"
        >
          <Dialog
            title={<>반려사유</>}
            open={props.modalToggle1}
            onClose={() => {
              props.setModalToggle1(false);
              props.setRejectTargetRows?.([]);
            }}
            contentSx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            fullWidth
            saveButtonName={'반려처리'}
            closeButtonName={'닫기'}
            useSaveButton={true}
            useSaveAfterClose={false}
            maxWidth="sm"
            onSave={() => {
              const reason = props.rejectReason ?? ''; // undefined/null 방지
              if (typeof reason === 'string' && reason.trim()) {
                props.RejectLogic(reason.trim());
                props.setModalToggle1(false); // 정상 입력 시에만 닫기
              } else {
                // 경고 메시지 띄우고 모달은 닫지 않음
                props.dispatch(
                  props.showSnackbar({
                    message: '반려 사유를 입력하세요.',
                    type: 'warning',
                  })
                );
              }
            }}
            content={
              <TextField
                label="반려 사유를 입력하세요"
                multiline
                rows={6}
                variant="outlined"
                fullWidth
                value={props.rejectReason}
                onChange={(e) => props.setRejectReason(e.target.value)}
                placeholder="사유를 입력하세요"
                inputProps={{ maxLength: 256 }}
              />
            }
          />
        </div>
      )}

      {props.modalToggle2 && (
        <div
          role="presentation"
          className="MuiDialog-root MuiModal-root"
          inert="true"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Dialog
            title="서명등록"
            open={props.modalToggle2}
            onClose={() => {
              props.setModalToggle2(false);
              props.setApprovalTargetRows?.([]);
              props.setPendingApprovalGubun?.('');
            }}
            contentSx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              padding: 3,
              maxHeight: '90vh',
              overflow: 'auto',
            }}
            fullWidth
            maxWidth="sm" // 팝업 너비 확장
            saveButtonName={null} // 별도 확인 버튼 아래 넣었기 때문에 숨김
            closeButtonName={'닫기'}
            useSaveButton={false}
            useSaveAfterClose={false}
            content={
              <Stack gap={2}>
                {/* 헤더 우측 재설정 버튼 */}
                <Box display="flex" justifyContent="flex-end">
                  <Chip
                    sx={{ borderRadius: '8px' }}
                    label="재설정"
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      props.signatureCanvas.current.clear();
                      props.setSignInputs({
                        mapUrl: '',
                        mapFiles: {},
                      });
                    }}
                  />
                </Box>

                {/* 서명 캔버스 영역 */}
                <Box
                  sx={{
                    border: '1px solid #ccc',
                    borderRadius: 2,
                    padding: 1,
                  }}
                >
                  <Canvas
                    signatureCanvas={props.signatureCanvas}
                    userId={props.userInfo?.userNm}
                    imageUrl={props.signInputs.mapUrl}
                    onEnd={(e) => {
                      if (props.signatureCanvas.current) {
                        const signatureDataUrl =
                          props.signatureCanvas.current.toDataURL('image/png');

                        const decodedData = atob(
                          signatureDataUrl.split(',')[1]
                        );
                        const byteNumbers = new Array(decodedData.length);
                        for (let i = 0; i < decodedData.length; i++) {
                          byteNumbers[i] = decodedData.charCodeAt(i);
                        }
                        const byteArray = new Uint8Array(byteNumbers);
                        const blob = new Blob([byteArray], {
                          type: 'image/png',
                        });

                        props.setSignInputs({
                          mapUrl: signatureDataUrl,
                          mapFiles: new File(
                            [blob],
                            `${new Date()
                              .toISOString()
                              .replace(/T/, '')
                              .replace(/\..+/, '')
                              .replace(/-/g, '')
                              .replace(/:/g, '')}.png`,
                            {
                              type: 'image/png',
                            }
                          ),
                        });
                      }
                    }}
                  />
                </Box>

                {/* 안내 문구 */}
                <Typography
                  sx={{ textAlign: 'center', color: 'text.secondary' }}
                >
                  [필수] 서명 정보 수집 및 이용에 동의합니다.
                </Typography>

                {/* 확인 버튼 */}
                <Button
                  sx={{
                    width: '100%',
                    height: '36px',
                    borderRadius: '18px',
                    backgroundColor: '#2DB400',
                    '&:hover': {
                      backgroundColor: '#26a300',
                    },
                  }}
                  onClick={async () => {
                    const gubun = props.pendingApprovalGubun || '2';
                    await props.ApproveLogic(
                      gubun,
                      props.signInputs.mapUrl,
                      props.approvalTargetRows || []
                    );
                  }}
                  name="확인"
                >
                  확인
                </Button>
              </Stack>
            }
          />
        </div>
      )}

      {props.modalToggle3 && (
        <div
          role="presentation"
          className="MuiDialog-root MuiModal-root"
          inert="true"
        >
          <Dialog
            title={
              <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>
                반려이력
              </Typography>
            }
            open={props.modalToggle3}
            onClose={(e) => props.setModalToggle3(false)}
            sx={{ '& .MuiDialog-paper': { overflow: 'hidden' } }}
            PaperProps={{
              sx: {
                borderRadius: 2,
              },
            }}
            titleSx={{
              px: 2.5,
              py: 1.75,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
            actionsSx={{
              px: 2,
              py: 1.25,
              borderTop: '1px solid',
              borderColor: 'divider',
              backgroundColor: '#fafafa',
            }}
            contentSx={{ display: 'flex', flexDirection: 'column', p: 0 }}
            fullWidth
            closeButtonName={'닫기'}
            useSaveAfterClose={true}
            maxWidth="md"
            onSave={() => {
              //handleManageSelection();
            }}
            content={
              <Box sx={{ p: 2.5, pt: 2 }}>
                <Box
                  className="ag-theme-alpine no-scroll"
                  sx={{
                    height: 560,
                    width: '100%',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    backgroundColor: '#fff',
                  }}
                >
                  <AgGridReact
                    ref={props.gridRef4}
                    animationRows={true}
                    columnDefs={columnDefsRejectHistory}
                    rowData={props.modalRowData3}
                    defaultColDef={{
                      cellDataType: false,
                      minWidth: 100,
                      editable: false,
                      resizable: true,
                      sortable: true,
                      filter: true,
                      floatingFilter: true,
                    }}
                    getRowStyle={(e) => GridFunctions.getRowStyle(e)}
                  />
                </Box>
              </Box>
            }
          />
        </div>
      )}

      <ConfirmDialog
        open={props.deleteMasterConfirmOpen || false}
        title="작업허가서 전체 삭제"
        content={
          <span>
            {props.deleteMasterConfirmCount > 1
              ? `선택한 작업허가서 ${props.deleteMasterConfirmCount}건을 삭제하시겠습니까?`
              : '선택한 작업허가서를 삭제하시겠습니까?'}
            <br />
            삭제 후에는 복구할 수 없습니다.
          </span>
        }
        onSave={props.onConfirmDeleteMasterPermit}
        onClose={() => props.setDeleteMasterConfirmOpen?.(false)}
        saveButtonName="삭제"
        closeButtonName="취소"
      />
    </>
  );
}

export default SAM_BASIC_02;

/** 조회조건 부서 콤보 — 전체 옵션 (value: '') */
export const SAM_BASIC_02_DEP_ALL_OPTION = { value: '', name: '전체' };

export function BuildSamBasic02DepComboItems(depList = []) {
  const list = Array.isArray(depList) ? depList : [];
  if (list.some((item) => item?.value === '')) {
    return list;
  }
  return [SAM_BASIC_02_DEP_ALL_OPTION, ...list];
}
